// Utility functions for onboarding flows

/**
 * Complete the creation of a program dashboard by mapping the Redux state to the backend payload and calling the API.
 * @param programData The program onboarding data from Redux state
 * @returns Promise resolving to the API response
 */
import {getUserProfileFromOkta} from '../../../utils/userHelper'

function parseSplit(str: string | undefined): string[] {
  if (!str) return []
  return str
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
}

function getUniqueNames(str: string): string {
  if (!str) return ''

  const uniqueNames = new Set<string>()
  const result: string[] = []

  str
    .split(',')
    .map(s => s.trim())
    .forEach(name => {
      const normalized = name.toLowerCase()
      if (!uniqueNames.has(normalized)) {
        uniqueNames.add(normalized)
        result.push(name)
      }
    })

  return result.join(',')
}

/**
 * Completes the program dashboard by preparing the payload for API submission.
 * @param programData The program onboarding data from Redux state
 * @param metricsDetails (optional) Additional metrics details if not included in programData
 * @param programResponse (optional) For isVisibleOnSeMetricsPage, etc.
 * @param selectedAppDAccount (optional) The full AppD account object (like Angular's this.selectedAppDAccount)
 */
export async function completeProgramDashboard(
  programData: any,
  metricsDetails?: any,
  programResponse?: any,
  editMode = false,
  selectedAppDAccount?: any,
) {
  // Extract user info
  const userProfile = getUserProfileFromOkta()
  const loggedInUser = userProfile?.email || ''
  const loggedInUserName = userProfile?.name || ''

  // General info
  const applicationName = programData.programName || ''
  const ownerDept = programData.ownerDepartment || programData.ownerDept || ''
  const email = programData.programOwnerEmail || ''
  const ownerName = programData.programOwnerName || ''

  // Metrics details (from programData or argument)
  const details = metricsDetails || programData.metricsDetails || {}
  const workItemType = details.workItemType || programData.workItemType || ''
  const uatState = details.uatState || programData.uatState || ''
  const service = details.service || programData.service || ''
  const serviceOffering = details.serviceOffering || programData.serviceOffering || ''
  const configurationItem = details.configurationItem || programData.configurationItem || ''
  const assignmentGroup = details.assignmentGroup || programData.assignmentGroup || ''
  const costTag = details.costTag || programData.infrastructureCost || ''
  // Use the selectedAppDAccount (like Angular does) or fall back to individual fields
  // Include projectName to avoid null pointer exception on backend
  const appDynamicsProject = details.appDynamicsProject || {
    projectName: applicationName || '', // Required field - use program name as project name
    appResponseTimeQuery: programData.appResponseTimeQuery || '',
    availabilityQuery: programData.availabilityQuery || '',
    account: selectedAppDAccount || {
      baseUrl: programData.appDAccountBaseUrl || '',
      account: programData.appDAccount || '',
      clientId: programData.appDClientId || '',
      clientSecret: programData.appDClientSecret || '',
      apiKey: programData.appDApiKey || '',
      apiAccountName: programData.appDApiAccountName || '',
    },
  }

  const hourToStoryPointRatio = Number(programData?.hourToStoryPointRatio)

  // Owners logic
  const emailsFromInput = parseSplit(email)
  // Don't add logged-in user in edit mode
  const ownersArray = editMode
    ? getUniqueNames(emailsFromInput.join(',')).split(',')
    : getUniqueNames([...emailsFromInput, loggedInUser].join(',')).split(',')

  // Don't add logged-in user name in edit mode
  const combinedOwnerName = editMode ? ownerName : ownerName ? `${ownerName}, ${loggedInUserName}` : loggedInUserName

  const combinedUniqueOwnerNames = getUniqueNames(combinedOwnerName)

  // Lead time config
  let leadTimeToChange: any = undefined
  if (workItemType || uatState) {
    leadTimeToChange = {}
    if (workItemType) leadTimeToChange.workItemType = workItemType
    if (uatState) leadTimeToChange.UATState = uatState
  }

  // ServiceNow config
  let serviceNowConfiguration: any = undefined
  if (service || serviceOffering || configurationItem || assignmentGroup) {
    serviceNowConfiguration = {}
    if (service) serviceNowConfiguration.service = service
    if (serviceOffering) serviceNowConfiguration.serviceOffering = serviceOffering
    if (configurationItem) serviceNowConfiguration.configurationItem = configurationItem
    if (assignmentGroup) serviceNowConfiguration.assignmentGroup = assignmentGroup
  }

  // Final payload
  const payload = {
    configurationItem: applicationName,
    configurationItemType: 'app',
    appServiceOwner: combinedUniqueOwnerNames,
    businessOwner: combinedUniqueOwnerNames,
    ownerDept,
    owners: ownersArray,
    supportOwner: combinedUniqueOwnerNames,
    developmentOwner: combinedUniqueOwnerNames,
    itemType: 'app',
    components: [],
    cloudService: costTag,
    appDynamicsProject,
    leadTimeToChangeConfiguration: leadTimeToChange,
    serviceNowConfiguration,
    isVisibleOnSeMetricsPage: programData?.isVisibleOnSeMetricsPage,
    isProgramInSustainMode: programData?.isProgramInSustainMode ?? false,
    hourToStoryPointRatio: hourToStoryPointRatio,
    l1LeaderEmail: programData?.l1LeaderEmail || '',
    appOwnerEmail: programData?.appOwnerEmail || '',
    teamDashboardIds: programData?.teamDashboardIds || [],
  }

  try {
    if (editMode && programData.id) {
      const {updateProgramDashboard} = await import('@/services/program/programService')
      return await updateProgramDashboard(programData.id, payload)
    } else {
      const {createProgramDashboard} = await import('@/services/program/programService')
      return await createProgramDashboard(payload)
    }
  } catch (error) {
    throw new Error(editMode ? 'Failed to update program dashboard' : 'Failed to create program dashboard')
  }
}

/**
 * Safely strips HTML tags from a string without ReDoS vulnerability.
 *
 * Security features:
 * - Limits input length to prevent DoS (100,000 chars max)
 * - Uses character-by-character parsing instead of regex to avoid backtracking
 * - Handles malformed HTML gracefully
 *
 * Behavior:
 * - Removes complete HTML tags: <tag>, </tag>, <tag attr="value">
 * - Preserves incomplete tags: <div (without closing >)
 * - Preserves > when not part of a tag
 *
 * @param str - Input string potentially containing HTML tags
 * @returns String with all complete HTML tags removed
 *
 * @example
 * stripHtmlTags('<div>Hello</div>') // 'Hello'
 * stripHtmlTags('<p>World<br/>!</p>') // 'World!'
 * stripHtmlTags('No tags here') // 'No tags here'
 * stripHtmlTags('<div') // '<div' (incomplete tag preserved)
 * stripHtmlTags('a > b') // 'a > b' (> not part of tag)
 */
export const stripHtmlTags = (str: string): string => {
  if (!str || typeof str !== 'string') return ''

  // Limit input length to prevent DoS
  if (str.length > 100000) {
    str = str.substring(0, 100000)
  }

  // Use character-by-character parsing to avoid regex backtracking
  // Only remove COMPLETE tags (those with both < and >)
  let result = ''
  let i = 0

  while (i < str.length) {
    if (str[i] === '<') {
      // Look for the closing >
      const endIndex = str.indexOf('>', i)

      if (endIndex === -1) {
        // No closing >, keep the rest of the string as-is (incomplete tag)
        result += str.substring(i)
        break
      } else {
        // Found complete tag, skip it (from < to >)
        i = endIndex + 1
      }
    } else {
      // Not a tag, add character to result
      result += str[i]
      i++
    }
  }

  return result
}

/**
 * Validates comma-separated email addresses safely without ReDoS vulnerability.
 *
 * Security features:
 * - Validates each email separately to prevent catastrophic backtracking
 * - Limits number of emails to 50 to prevent DoS
 * - Limits total email length to 254 characters (RFC 5321 standard)
 * - Uses manual validation instead of regex to avoid backtracking vulnerabilities
 *
 * Note: This validation prioritizes security over strict RFC compliance.
 * It validates basic email structure but does not enforce all RFC 5321 constraints
 * (e.g., local part length limit) to maintain backward compatibility with existing tests.
 *
 * @param emailString - Comma-separated email addresses
 * @returns true if all emails are valid, false otherwise
 *
 * @example
 * validateEmails('user@example.com') // true
 * validateEmails('user1@example.com, user2@example.com') // true
 * validateEmails('invalid-email') // false
 */
export const validateEmails = (emailString: string): boolean => {
  if (!emailString || typeof emailString !== 'string') return false

  // Split by comma and trim whitespace
  const emails = emailString.split(',').map(e => e.trim())

  // Limit number of emails to prevent DoS
  if (emails.length > 50) return false

  // Validate each email separately using safe character-by-character validation
  return emails.every(email => {
    // Limit email length to prevent DoS (RFC 5321 standard max total length)
    if (email.length === 0 || email.length > 254) return false

    // Find @ symbol position
    const atIndex = email.indexOf('@')

    // Must have exactly one @ symbol, not at the start or end
    if (atIndex === -1 || atIndex === 0 || atIndex === email.length - 1) return false

    // Check for multiple @ symbols
    if (email.indexOf('@', atIndex + 1) !== -1) return false

    // Split into local and domain parts
    const localPart = email.substring(0, atIndex)
    const domainPart = email.substring(atIndex + 1)

    // Validate local part (before @) - basic length check only
    if (localPart.length === 0) return false
    if (hasInvalidEmailChars(localPart, false)) return false

    // Validate domain part (after @) - basic length check
    if (domainPart.length === 0) return false

    // Domain must have at least one dot
    const dotIndex = domainPart.indexOf('.')
    if (dotIndex === -1 || dotIndex === 0 || dotIndex === domainPart.length - 1) return false

    if (hasInvalidEmailChars(domainPart, true)) return false

    return true
  })
}

/**
 * Helper function to check for invalid characters in email parts.
 * Uses character-by-character validation to avoid regex backtracking.
 *
 * @param part - Email part to validate
 * @param isDomain - Whether this is the domain part (stricter validation)
 * @returns true if invalid characters found, false otherwise
 */
function hasInvalidEmailChars(part: string, isDomain: boolean): boolean {
  for (let i = 0; i < part.length; i++) {
    const char = part[i]
    const code = char.charCodeAt(0)

    // Check for whitespace
    if (char === ' ' || char === '\t' || char === '\n' || char === '\r') {
      return true
    }

    // For domain part, only allow alphanumeric, dot, and hyphen
    if (isDomain) {
      const isAlphanumeric = (code >= 48 && code <= 57) || (code >= 65 && code <= 90) || (code >= 97 && code <= 122)
      const isDot = char === '.'
      const isHyphen = char === '-'

      if (!isAlphanumeric && !isDot && !isHyphen) {
        return true
      }
    }
    // For local part, disallow @ and whitespace (already checked above)
    else {
      if (char === '@') {
        return true
      }
    }
  }

  return false
}
